import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./index.css";
import scenarioStudio from "../../image/scenarios/studio.webp";
import scenarioRetail from "../../image/scenarios/retail.webp";
import scenarioMarket from "../../image/scenarios/market.webp";
import scenarioEnterprise from "../../image/scenarios/enterprise.webp";
import scenarioEcommerce from "../../image/scenarios/ecommerce.webp";
import scenarioStore from "../../image/scenarios/store.webp";
import scenarioFactory from "../../image/scenarios/factory.webp";
import scenarioAdvertising from "../../image/scenarios/advertising.webp";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import HeroCarousel from "../../components/HeroCarousel";
import DeferredMount from "../../components/DeferredMount";
import DeferredServiceGlobe from "../../components/DeferredServiceGlobe";
import { useLocale } from "../../i18n/LocaleContext";
import { useLocalePath } from "../../i18n/useLocalePath";
import ProductCategoryNav from "../../components/ProductCategoryNav";
import { productCategories } from "../../data/productCategories";
import { showcaseProducts } from "../../data/products";
import { useActiveEquipmentCategory } from "../../i18n/ActiveEquipmentCategoryContext";

const scenarios = [
  {
    id: "studio",
    titleKey: "scenarioStudio",
    descKey: "scenarioStudioDesc",
    image: scenarioStudio,
  },
  {
    id: "retail",
    titleKey: "scenarioRetail",
    descKey: "scenarioRetailDesc",
    image: scenarioRetail,
  },
  {
    id: "market",
    titleKey: "scenarioMarket",
    descKey: "scenarioMarketDesc",
    image: scenarioMarket,
  },
  {
    id: "enterprise",
    titleKey: "scenarioEnterprise",
    descKey: "scenarioEnterpriseDesc",
    image: scenarioEnterprise,
  },
  {
    id: "ecommerce",
    titleKey: "scenarioEcommerce",
    descKey: "scenarioEcommerceDesc",
    image: scenarioEcommerce,
  },
  {
    id: "store",
    titleKey: "scenarioStore",
    descKey: "scenarioStoreDesc",
    image: scenarioStore,
  },
  {
    id: "factory",
    titleKey: "scenarioFactory",
    descKey: "scenarioFactoryDesc",
    image: scenarioFactory,
  },
  {
    id: "advertising",
    titleKey: "scenarioAdvertising",
    descKey: "scenarioAdvertisingDesc",
    image: scenarioAdvertising,
  },
];

const DEFAULT_PRODUCT_START_ID = {
  uv: "uv-6090",
};

/** 分类内优先展示指定机型（如 UV 的 6090） */
function orderProductsForCategory(products, categoryId) {
  const preferredId = DEFAULT_PRODUCT_START_ID[categoryId];
  if (!preferredId) return products;
  const index = products.findIndex((item) => item.id === preferredId);
  if (index <= 0) return products;
  return [
    products[index],
    ...products.slice(0, index),
    ...products.slice(index + 1),
  ];
}

function getCategoryStartIndex(products, categoryId) {
  const preferredId = DEFAULT_PRODUCT_START_ID[categoryId];
  if (!preferredId) return 0;
  const index = products.findIndex((item) => item.id === preferredId);
  return index >= 0 ? index : 0;
}

function Home() {
  const { t, language } = useLocale();
  const localePath = useLocalePath();
  const [activeCategory, setActiveCategory] = useState(productCategories[0].id);
  const [productIndex, setProductIndex] = useState(() =>
    getCategoryStartIndex(
      orderProductsForCategory(
        showcaseProducts.filter(
          (item) => item.category === productCategories[0].id
        ),
        productCategories[0].id
      ),
      productCategories[0].id
    )
  );
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isProductHovered, setIsProductHovered] = useState(false);
  const [isProductFocused, setIsProductFocused] = useState(false);
  const [slideDirection, setSlideDirection] = useState("next");
  const [slideAnimKey, setSlideAnimKey] = useState(0);
  const [mobileStackIndex, setMobileStackIndex] = useState(0);
  const [mobileDragY, setMobileDragY] = useState(0);
  const [mobileDragging, setMobileDragging] = useState(false);
  const [mobilePageHeight, setMobilePageHeight] = useState(0);
  const dragRef = useRef({ active: false, startX: 0, deltaX: 0 });
  const suppressClickRef = useRef(false);
  const productSectionRef = useRef(null);
  const mobileProductScrollerRef = useRef(null);
  const mobilePagerRef = useRef({
    startY: 0,
    lastY: 0,
    lastTime: 0,
    deltaY: 0,
    velocity: 0,
    handingToPage: false,
    ignore: false,
    index: 0,
    raf: 0,
    active: false,
    pointerId: null,
    pageHeight: 0,
    lockScrollY: 0,
  });
  const { setActiveCategory: setGlobalCategory } = useActiveEquipmentCategory();

  const filteredProducts = orderProductsForCategory(
    showcaseProducts.filter((item) => item.category === activeCategory),
    activeCategory
  );
  const activeProduct =
    filteredProducts[productIndex] || filteredProducts[0] || null;

  useEffect(() => {
    mobilePagerRef.current.index = mobileStackIndex;
  }, [mobileStackIndex]);

  useEffect(() => {
    const scroller = mobileProductScrollerRef.current;
    if (!scroller) return undefined;

    const update = () => {
      const h = scroller.clientHeight || scroller.getBoundingClientRect().height || 0;
      if (h <= 0) return;
      mobilePagerRef.current.pageHeight = h;
      setMobilePageHeight((prev) => (prev === h ? prev : h));
    };

    update();
    window.addEventListener("resize", update);
    let observer;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(update);
      observer.observe(scroller);
    }
    // 延迟再量一次，避免手机端样式尚未生效时高度为 0
    const timer = window.setTimeout(update, 100);

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("resize", update);
      observer?.disconnect();
    };
  }, [activeCategory, filteredProducts.length]);

  useEffect(() => {
    setGlobalCategory(activeCategory);
    return () => setGlobalCategory(null);
  }, [activeCategory, setGlobalCategory]);

  useEffect(() => {
    const nextIndex = getCategoryStartIndex(filteredProducts, activeCategory);
    setProductIndex(nextIndex);
    setMobileStackIndex(0);
    setMobileDragY(0);
    setMobileDragging(false);
    setDragOffset(0);
    setIsDragging(false);
    setSlideDirection("next");
    setSlideAnimKey((key) => key + 1);
    dragRef.current = { active: false, startX: 0, deltaX: 0 };
    mobilePagerRef.current = {
      ...mobilePagerRef.current,
      startY: 0,
      lastY: 0,
      lastTime: 0,
      deltaY: 0,
      velocity: 0,
      handingToPage: false,
      ignore: false,
      index: 0,
    };
  }, [activeCategory]);

  useEffect(() => {
    if (mobileStackIndex > filteredProducts.length - 1) {
      setMobileStackIndex(Math.max(0, filteredProducts.length - 1));
    }
  }, [mobileStackIndex, filteredProducts.length]);

  /* 手机竖滑：pointer 事件（兼容真机触摸 + 模拟器鼠标），按实际高度位移 */
  useEffect(() => {
    const scroller = mobileProductScrollerRef.current;
    if (!scroller) return undefined;

    const pager = mobilePagerRef.current;
    const total = () => filteredProducts.length;

    const measureHeight = () => {
      const h = scroller.clientHeight || scroller.getBoundingClientRect().height || 0;
      if (h > 0) {
        pager.pageHeight = h;
        setMobilePageHeight((prev) => (prev === h ? prev : h));
      }
      return pager.pageHeight || h || 1;
    };

    const lockPageScroll = () => {
      const y = window.scrollY || document.documentElement.scrollTop || 0;
      if (y !== pager.lockScrollY) {
        window.scrollTo(0, pager.lockScrollY);
      }
    };

    const onPointerDown = (event) => {
      if (event.pointerType === "mouse" && event.button !== 0) return;
      if (event.target.closest("a, button")) {
        pager.ignore = true;
        return;
      }
      pager.ignore = false;
      pager.handingToPage = false;
      pager.active = true;
      pager.pointerId = event.pointerId;
      measureHeight();
      pager.startY = event.clientY;
      pager.lastY = pager.startY;
      pager.lastTime = event.timeStamp || Date.now();
      pager.deltaY = 0;
      pager.velocity = 0;
      pager.lockScrollY = window.scrollY || document.documentElement.scrollTop || 0;
      if (pager.raf) {
        cancelAnimationFrame(pager.raf);
        pager.raf = 0;
      }
      try {
        scroller.setPointerCapture(event.pointerId);
      } catch (_) {
        /* ignore */
      }
      setMobileDragging(true);
      setMobileDragY(0);
    };

    const onPointerMove = (event) => {
      if (!pager.active || pager.ignore) return;
      if (pager.pointerId != null && event.pointerId !== pager.pointerId) return;

      const y = event.clientY;
      const now = event.timeStamp || Date.now();
      const frameDelta = y - pager.lastY;
      const dt = Math.max(1, now - pager.lastTime);
      pager.velocity = frameDelta / dt;
      pager.lastY = y;
      pager.lastTime = now;

      const deltaY = y - pager.startY;
      const index = pager.index;
      const last = Math.max(0, total() - 1);
      const atFirst = index <= 0;
      const atLast = index >= last;

      // 末尾再上滑 / 开头再下滑 → 交给整页
      if ((atLast && deltaY < -10) || (atFirst && deltaY > 10) || pager.handingToPage) {
        pager.handingToPage = true;
        pager.deltaY = 0;
        if (pager.raf) {
          cancelAnimationFrame(pager.raf);
          pager.raf = 0;
        }
        setMobileDragY(0);
        window.scrollBy(0, -frameDelta);
        event.preventDefault();
        return;
      }

      // 产品区内竖滑：锁住页面滚动，只切换产品
      lockPageScroll();
      let nextDrag = deltaY;
      if ((atFirst && nextDrag > 0) || (atLast && nextDrag < 0)) {
        nextDrag *= 0.35;
      }
      pager.deltaY = nextDrag;
      if (!pager.raf) {
        pager.raf = requestAnimationFrame(() => {
          pager.raf = 0;
          setMobileDragY(pager.deltaY);
        });
      }
      event.preventDefault();
    };

    // iOS/部分浏览器仍走 touchmove 滚动整页，需非 passive 才能拦住
    const onTouchMove = (event) => {
      if (!pager.active || pager.ignore) return;
      if (pager.handingToPage) return;
      event.preventDefault();
      lockPageScroll();
    };

    const finishPointer = (event) => {
      if (pager.pointerId != null && event.pointerId !== pager.pointerId) return;
      if (pager.raf) {
        cancelAnimationFrame(pager.raf);
        pager.raf = 0;
      }

      const wasActive = pager.active;
      const wasHanding = pager.handingToPage;
      pager.active = false;
      pager.pointerId = null;

      if (pager.ignore) {
        pager.ignore = false;
        setMobileDragging(false);
        setMobileDragY(0);
        return;
      }
      if (!wasActive) return;

      const deltaY = pager.deltaY;
      const velocity = pager.velocity;
      const last = Math.max(0, total() - 1);
      const h = measureHeight();
      const threshold = Math.min(48, h * 0.12);
      const fast = Math.abs(velocity) > 0.28;

      pager.deltaY = 0;
      pager.handingToPage = false;

      // 已交给整页滚动，或位移太小：回弹到当前页
      if (wasHanding || (Math.abs(deltaY) < 6 && !fast)) {
        setMobileDragging(false);
        setMobileDragY(0);
        return;
      }

      let next = pager.index;
      if (deltaY < -threshold || (fast && velocity < -0.28)) {
        next = Math.min(pager.index + 1, last);
        suppressClickRef.current = true;
      } else if (deltaY > threshold || (fast && velocity > 0.28)) {
        next = Math.max(pager.index - 1, 0);
        suppressClickRef.current = true;
      }

      // 保持当前视觉位置，再过渡到目标页，避免先回弹再翻页的顿挫
      const visual = -pager.index * h + deltaY;
      const settleDrag = visual + next * h;
      pager.index = next;
      setMobileStackIndex(next);
      setMobileDragY(settleDrag);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setMobileDragging(false);
          setMobileDragY(0);
        });
      });
    };

    const moveOpts = { capture: true, passive: false };
    scroller.addEventListener("pointerdown", onPointerDown, { capture: true });
    scroller.addEventListener("pointermove", onPointerMove, moveOpts);
    scroller.addEventListener("touchmove", onTouchMove, moveOpts);
    scroller.addEventListener("pointerup", finishPointer, { capture: true });
    scroller.addEventListener("pointercancel", finishPointer, { capture: true });

    // 首屏立刻量一次高度，避免 transform 乘 0 导致“滑不动”
    measureHeight();

    return () => {
      if (pager.raf) cancelAnimationFrame(pager.raf);
      pager.active = false;
      scroller.removeEventListener("pointerdown", onPointerDown, true);
      scroller.removeEventListener("pointermove", onPointerMove, true);
      scroller.removeEventListener("touchmove", onTouchMove, true);
      scroller.removeEventListener("pointerup", finishPointer, true);
      scroller.removeEventListener("pointercancel", finishPointer, true);
    };
  }, [filteredProducts.length, activeCategory]);

  useEffect(() => {
    if (productIndex > filteredProducts.length - 1) {
      setProductIndex(0);
    }
  }, [productIndex, filteredProducts.length]);

  useEffect(() => {
    if (
      filteredProducts.length <= 1 ||
      isDragging ||
      isProductHovered ||
      isProductFocused
    ) {
      return undefined;
    }

    const timer = setInterval(() => {
      setSlideDirection("next");
      setSlideAnimKey((key) => key + 1);
      setProductIndex((prev) => (prev + 1) % filteredProducts.length);
    }, 5500);

    return () => clearInterval(timer);
  }, [
    filteredProducts.length,
    isDragging,
    isProductHovered,
    isProductFocused,
    activeCategory,
    productIndex,
  ]);

  useEffect(() => {
    const section = productSectionRef.current;
    if (!section) return undefined;

    const handleKeyDown = (event) => {
      if (filteredProducts.length <= 1) return;
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        setSlideDirection("prev");
        setSlideAnimKey((key) => key + 1);
        setProductIndex((prev) =>
          prev === 0 ? filteredProducts.length - 1 : prev - 1
        );
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        setSlideDirection("next");
        setSlideAnimKey((key) => key + 1);
        setProductIndex((prev) => (prev + 1) % filteredProducts.length);
      }
    };

    section.addEventListener("keydown", handleKeyDown);
    return () => section.removeEventListener("keydown", handleKeyDown);
  }, [filteredProducts.length]);

  const changeProduct = (nextIndex, direction) => {
    if (filteredProducts.length <= 1) return;
    const total = filteredProducts.length;
    const normalized = ((nextIndex % total) + total) % total;
    if (normalized === productIndex) return;
    setSlideDirection(direction);
    setSlideAnimKey((key) => key + 1);
    setProductIndex(normalized);
  };

  const goPrevProduct = () => {
    changeProduct(productIndex - 1, "prev");
  };

  const goNextProduct = () => {
    changeProduct(productIndex + 1, "next");
  };

  const goToProduct = (index) => {
    if (index === productIndex) return;
    changeProduct(index, index > productIndex ? "next" : "prev");
  };

  const handleProductPointerDown = (event) => {
    if (filteredProducts.length <= 1) return;
    if (event.target.closest("a, button")) return;
    dragRef.current = {
      active: true,
      startX: event.clientX,
      deltaX: 0,
    };
    setIsDragging(true);
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handleProductPointerMove = (event) => {
    if (!dragRef.current.active) return;
    const deltaX = event.clientX - dragRef.current.startX;
    dragRef.current.deltaX = deltaX;
    setDragOffset(deltaX);
  };

  const finishProductDrag = () => {
    if (!dragRef.current.active) return;
    const { deltaX } = dragRef.current;
    dragRef.current = { active: false, startX: 0, deltaX: 0 };
    setIsDragging(false);
    setDragOffset(0);

    const threshold = 60;
    suppressClickRef.current = Math.abs(deltaX) > 10;
    if (deltaX > threshold) {
      goPrevProduct();
    } else if (deltaX < -threshold) {
      goNextProduct();
    }
  };

  const renderProductCardBody = (product, options = {}) => {
    const { interactive = false, nearby = false } = options;
    const shouldLoadEager = interactive || nearby;
    return (
      <>
        <div className="product-showcase-media">
          <img
            src={product.image}
            alt={product.name}
            className="product-showcase-image"
            draggable={false}
            decoding="async"
            loading={shouldLoadEager ? "eager" : "lazy"}
            fetchPriority={interactive ? "high" : nearby ? "auto" : "low"}
          />
          <p className="product-showcase-model-on-image" aria-hidden="true">
            {product.name}
          </p>
        </div>
        {product.subtitleKey ? (
          <>
            <div className="product-showcase-overlay" />
            <div className="product-showcase-content">
              <div className="product-showcase-text">
                <h3>{product.name}</h3>
                <p className="product-showcase-subtitle">
                  {t(product.subtitleKey)}
                </p>
                {product.featureKeys ? (
                  <div className="product-showcase-features">
                    {product.featureKeys.map((key) => (
                      <span key={key}>{t(key)}</span>
                    ))}
                  </div>
                ) : null}
              </div>
              <Link
                to={`${localePath("/products")}?category=${encodeURIComponent(
                  product.category
                )}&product=${encodeURIComponent(product.id)}`}
                className="product-showcase-btn"
                draggable={false}
                onClick={(event) => {
                  if (suppressClickRef.current) {
                    event.preventDefault();
                    suppressClickRef.current = false;
                  }
                }}
              >
                {t("learnMore")}
              </Link>
            </div>
          </>
        ) : null}
      </>
    );
  };

  return (
    <div className="home-page">
      <Header />

      <HeroCarousel />

      <section
        className="section product-showcase-section"
        key={`core-products-${language}`}
        ref={productSectionRef}
        tabIndex={0}
        aria-label={t("coreProducts")}
        onFocus={() => setIsProductFocused(true)}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) {
            setIsProductFocused(false);
          }
        }}
      >
        <div className="container">
          <div className="section-title">
            <h2>{t("coreProducts")}</h2>
            <p>{t("coreProductsDesc")}</p>
          </div>

          <ProductCategoryNav
            activeCategory={activeCategory}
            onChange={setActiveCategory}
            className="home-product-category-nav"
          />

          {activeProduct && (
            <div
              className="product-showcase-desktop"
              onMouseEnter={() => setIsProductHovered(true)}
              onMouseLeave={() => setIsProductHovered(false)}
            >
              <div className="product-showcase-viewport">
                <article
                  className={`product-showcase-card ${
                    isDragging ? "is-dragging" : ""
                  } ${filteredProducts.length > 1 ? "is-swipeable" : ""} ${
                    !isDragging ? `slide-in-${slideDirection}` : ""
                  }`}
                  key={`${activeProduct.id}-${slideAnimKey}`}
                  style={{
                    transform: dragOffset
                      ? `translateX(${dragOffset}px)`
                      : undefined,
                  }}
                  onPointerDown={handleProductPointerDown}
                  onPointerMove={handleProductPointerMove}
                  onPointerUp={finishProductDrag}
                  onPointerCancel={finishProductDrag}
                  onPointerLeave={() => {
                    if (dragRef.current.active) finishProductDrag();
                  }}
                >
                  {renderProductCardBody(activeProduct, { interactive: true })}
                </article>
              </div>

              <div className="product-showcase-controls">
                <div className="product-showcase-dots">
                  {filteredProducts.map((item, index) => (
                    <button
                      key={item.id}
                      type="button"
                      className={`product-showcase-dot ${
                        productIndex === index ? "active" : ""
                      }`}
                      onClick={() => goToProduct(index)}
                      aria-label={`${item.name}`}
                    />
                  ))}
                </div>
                <div className="product-showcase-arrows">
                  <button
                    type="button"
                    className={`product-showcase-arrow ${
                      filteredProducts.length > 1 ? "active" : ""
                    }`}
                    onClick={goPrevProduct}
                    aria-label={t("carouselPrev")}
                    disabled={filteredProducts.length <= 1}
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    className={`product-showcase-arrow ${
                      filteredProducts.length > 1 ? "active" : ""
                    }`}
                    onClick={goNextProduct}
                    aria-label={t("carouselNext")}
                    disabled={filteredProducts.length <= 1}
                  >
                    ›
                  </button>
                </div>
              </div>
            </div>
          )}

          <div
            className={`product-showcase-mobile-scroller${
              mobileDragging ? " is-dragging" : ""
            }`}
            ref={mobileProductScrollerRef}
            aria-label={t("coreProducts")}
          >
            <div
              className={`product-showcase-mobile-track${
                mobileDragging ? " is-dragging" : ""
              }`}
              style={{
                transform: `translate3d(0, ${
                  -mobileStackIndex *
                    (mobilePageHeight ||
                      mobilePagerRef.current.pageHeight ||
                      0) +
                  mobileDragY
                }px, 0)`,
              }}
            >
              {filteredProducts.map((product, index) => (
                <article
                  key={product.id}
                  className="product-showcase-card product-showcase-card--stack"
                >
                  {renderProductCardBody(product, {
                    interactive: index === mobileStackIndex,
                    nearby: Math.abs(index - mobileStackIndex) === 1,
                  })}
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section scenarios-section">
        <div className="container">
          <div className="section-title">
            <h2>{t("scenariosTitle")}</h2>
            <p>{t("scenariosDesc")}</p>
          </div>
          <div className="scenarios-grid">
            {scenarios.map((item) => (
              <article className="scenario-card" key={item.id}>
                <div
                  className={`scenario-image-wrap${
                    item.image ? "" : " is-placeholder"
                  }`}
                >
                  {item.image ? (
                    <img
                      className="scenario-image"
                      src={item.image}
                      alt={t(item.titleKey)}
                      loading="lazy"
                      decoding="async"
                    />
                  ) : null}
                </div>
                <h3>{t(item.titleKey)}</h3>
                <p>{t(item.descKey)}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <DeferredMount
        loader={() => import("./HomeApplicationsMarquee")}
        fallback={<section id="applications" className="section applications-marquee-section" aria-hidden="true" />}
      />

      <DeferredMount
        loader={() => import("../../components/CustomerStories")}
        fallback={<section className="customer-stories-section" aria-hidden="true" />}
      />

      <section className="section service-partner-section">
        <div className="container">
          <div className="service-partner-header">
            <h2>{t("servicePartnerTitle")}</h2>
            <p>{t("servicePartnerDesc")}</p>
          </div>

          <div className="service-partner-layout">
            <div className="service-partner-map">
              <DeferredServiceGlobe />
            </div>

            <div className="service-partner-panels">
              <div className="service-partner-panel service-partner-panel-light">
                <div className="service-partner-item">
                  <h3>{t("serviceSupportTitle")}</h3>
                  <p>{t("serviceSupportDesc")}</p>
                </div>
                <div className="service-partner-item">
                  <h3>{t("servicePresaleTitle")}</h3>
                  <p>{t("servicePresaleDesc")}</p>
                </div>
                <div className="service-partner-item">
                  <h3>{t("serviceLifetimeTitle")}</h3>
                  <p>{t("serviceLifetimeDesc")}</p>
                </div>
              </div>

              <div className="service-partner-panel service-partner-panel-dark">
                <h3 className="service-partner-care-title">
                  {t("serviceCareTitle")}
                </h3>
                <div className="service-partner-care-item">
                  <span className="service-partner-check" aria-hidden="true">
                    ✓
                  </span>
                  <div>
                    <h4>{t("serviceCareDamageTitle")}</h4>
                    <p>{t("serviceCareDamageDesc")}</p>
                  </div>
                </div>
                <div className="service-partner-care-item">
                  <span className="service-partner-check" aria-hidden="true">
                    ✓
                  </span>
                  <div>
                    <h4>{t("serviceCareExchangeTitle")}</h4>
                    <p>{t("serviceCareExchangeDesc")}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default Home;